<script setup>
// Talkvia table for report pages. Renders a TanStack table instance with a
// clean header, subtle row dividers and optional (real) column sorting.
// Column options come from `columnDef.meta`: { align: 'end' } right-aligns
// numeric columns and applies tabular numbers.
import { FlexRender } from '@tanstack/vue-table';

defineProps({
  table: { type: Object, required: true },
  minWidthClass: { type: String, default: 'min-w-[36rem]' },
  scrollClass: { type: String, default: '' },
});

const isEnd = column => column.columnDef.meta?.align === 'end';

const ariaSort = header => {
  if (!header.column.getCanSort()) return undefined;
  const sorted = header.column.getIsSorted();
  if (sorted === 'asc') return 'ascending';
  if (sorted === 'desc') return 'descending';
  return 'none';
};

const sortIcon = header => {
  const sorted = header.column.getIsSorted();
  if (sorted === 'asc') return 'i-hugeicons-arrow-up-01 text-n-slate-12';
  if (sorted === 'desc') return 'i-hugeicons-arrow-down-01 text-n-slate-12';
  return 'i-hugeicons-arrow-up-down text-n-slate-9';
};

const toggleSort = (header, event) => {
  header.column.getToggleSortingHandler()?.(event);
};
</script>

<template>
  <div class="w-full overflow-x-auto" :class="scrollClass">
    <table
      class="w-full border-separate border-spacing-0 text-sm"
      :class="minWidthClass"
    >
      <thead>
        <tr
          v-for="headerGroup in table.getHeaderGroups()"
          :key="headerGroup.id"
        >
          <th
            v-for="header in headerGroup.headers"
            :key="header.id"
            scope="col"
            :aria-sort="ariaSort(header)"
            :style="
              header.column.columnDef.size
                ? { width: `${header.getSize()}px` }
                : undefined
            "
            class="sticky top-0 z-[1] border-y border-n-weak bg-n-solid-2 px-4 py-2.5 align-bottom text-xs font-medium leading-snug text-n-slate-11 first:ps-5 last:pe-5 dark:border-n-strong"
            :class="isEnd(header.column) ? 'text-right' : 'text-left'"
          >
            <template v-if="!header.isPlaceholder">
              <button
                v-if="header.column.getCanSort()"
                type="button"
                class="inline-flex items-end gap-1 rounded text-start text-xs font-medium text-n-slate-11 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                :class="{ 'flex-row-reverse': isEnd(header.column) }"
                @click="toggleSort(header, $event)"
              >
                <FlexRender
                  :render="header.column.columnDef.header"
                  :props="header.getContext()"
                />
                <span
                  :class="sortIcon(header)"
                  class="size-3.5 shrink-0"
                  aria-hidden="true"
                />
              </button>
              <FlexRender
                v-else
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
            </template>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in table.getRowModel().rows"
          :key="row.id"
          class="transition-colors hover:bg-n-alpha-1 motion-reduce:transition-none [&:not(:last-child)>td]:border-b [&:not(:last-child)>td]:border-n-weak dark:[&:not(:last-child)>td]:border-n-strong"
        >
          <td
            v-for="cell in row.getVisibleCells()"
            :key="cell.id"
            class="px-4 py-3 align-middle text-n-slate-12 first:ps-5 last:pe-5"
            :class="
              isEnd(cell.column)
                ? 'whitespace-nowrap text-right tabular-nums'
                : 'text-left'
            "
          >
            <FlexRender
              :render="cell.column.columnDef.cell"
              :props="cell.getContext()"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
